import { useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { PageHeader } from '@/components/PageHeader';
import { Button } from '@/components/Button';
import { Banner } from '@/components/Banner';
import { Card, Seccion, EstadoVacio } from '@/components/Card';
import { Falta } from '@/components/Falta';
import { Buscador } from '@/components/Buscador';
import { CampoCliente } from '@/components/CampoCliente';
import { TextField, NumberField, MoneyField, SelectField, DateField } from '@/components/Field';
import {
  crearPedido,
  editarPedido,
  alternarPago,
  alternarEntrega,
  cancelarPedido,
  reactivarPedido,
  eliminarPedido,
  verPedidosDeFecha,
  verEntregasDeFecha,
  verCuentasPorCobrar,
  conteoPorDia,
  fechasConPedidos,
  resumenDePedidosEntre,
  clientesUsados,
  recetasParaPedido,
  platillosParaLigar,
  precioUsadoCon,
  type RenglonPedido,
  type EntregasDeFecha,
} from '@/db/repos/orders';
import {
  ESTADOS_PAGO,
  ESTADOS_ENTREGA,
  elegirDiaVisible,
  separarPorCobrar,
  type ResumenPedidos,
  type DiaDePedidos,
  type CuentasPorCobrar,
  type CuentaDeCliente,
} from '@/domain/orders';
import {
  hoy,
  sumarDias,
  diasDeSemana,
  nombreDia,
  formatearCorto,
  formatearMedio,
  diaDelMes,
  nombreRelativo,
  esHoy,
  type FechaLocal,
} from '@/domain/dates';
import { formatearCentavos, pesosACentavos, centavosAPesos } from '@/domain/money';
import { mensajeDeError } from '@/db/tx';

type Vista =
  | { modo: 'lista' }
  | { modo: 'nuevo'; fecha: FechaLocal }
  | { modo: 'editar'; renglon: RenglonPedido };

/**
 * Las dos preguntas del modulo, separadas a proposito:
 *
 *   pedidos  = que pedi para este dia
 *   entregas = para quien es cada comida y cuales me faltan
 *   cuentas  = quien me debe y cuanto
 *
 * Son la MISMA informacion leida de tres maneras. No hay registros distintos:
 * marcar una entrega o un pago en cualquiera de ellas escribe en el mismo pedido.
 *
 * Las dos primeras trabajan sobre un dia. Cuentas NO: la deuda es acumulada, y
 * por eso ahi se oculta la navegacion por dia en lugar de dejarla sin efecto.
 */
type Pestana = 'pedidos' | 'entregas' | 'cuentas';

/**
 * Pedidos de clientes: a quien le toca cada plato, cuanto debe y si ya se lo
 * llevo.
 *
 * MODULO INDEPENDIENTE. Nada de lo que se haga aqui mueve inventario, genera
 * producciones ni registra ventas. Lo que se cocina sigue saliendo del menu.
 *
 * Por eso los totales se llaman "cobrado" y "por cobrar" y NUNCA ingreso: el
 * ingreso del negocio nace solo al registrar una venta, y tener dos cifras
 * distintas para el mismo dinero descuadraria las ganancias.
 */
export function OrdersPage() {
  // null significa "todavia no elige": manda el dia automatico. En cuanto toca
  // un dia, su eleccion queda fija y ya no se mueve sola.
  const [diaElegido, setDiaElegido] = useState<FechaLocal | null>(null);
  const [pestana, setPestana] = useState<Pestana>('pedidos');
  const [verSemana, setVerSemana] = useState(false);
  const [vista, setVista] = useState<Vista>({ modo: 'lista' });
  const [error, setError] = useState<string | null>(null);

  const conPedidos = useLiveQuery(() => fechasConPedidos(), []);

  const referencia = hoy();
  const dia =
    diaElegido ??
    (conPedidos === undefined ? referencia : elegirDiaVisible(referencia, conPedidos));

  // La semana del selector sigue al dia elegido, no tiene ancla propia: avanzar
  // de dia cruza a la semana siguiente sin que haya que moverla aparte.
  const dias = diasDeSemana(dia);
  const desde = dias[0] ?? dia;
  const hasta = dias[6] ?? dia;

  const delDia = useLiveQuery(() => verPedidosDeFecha(dia), [dia]);
  const entregas = useLiveQuery(() => verEntregasDeFecha(dia), [dia]);
  const conteo = useLiveQuery(() => conteoPorDia(desde, hasta), [desde, hasta]);
  const resumenSemana = useLiveQuery(() => resumenDePedidosEntre(desde, hasta), [desde, hasta]);
  const cuentas = useLiveQuery(() => verCuentasPorCobrar(), []);

  const manana = sumarDias(referencia, 1);

  /** Al guardar se aterriza en el dia del pedido, no en el que estaba antes. */
  function volverAlDia(fecha: FechaLocal) {
    setDiaElegido(fecha);
    setVista({ modo: 'lista' });
  }

  if (vista.modo === 'nuevo') {
    return (
      <PedidoForm
        fechaInicial={vista.fecha}
        onListo={volverAlDia}
        onCancelar={() => setVista({ modo: 'lista' })}
      />
    );
  }

  if (vista.modo === 'editar') {
    return (
      <PedidoForm
        renglon={vista.renglon}
        fechaInicial={vista.renglon.fechaEntrega}
        onListo={volverAlDia}
        onCancelar={() => setVista({ modo: 'lista' })}
      />
    );
  }

  return (
    <div className="space-y-4">
      <PageHeader titulo="Pedidos" descripcion="Quién pidió, cuánto debe y si ya se entregó" />

      {error !== null && (
        <Banner tono="alerta" titulo="No se pudo completar">
          {error}
        </Banner>
      )}

      {/* Las tres lecturas de los mismos pedidos. */}
      <div className="grid grid-cols-3 gap-1 rounded-xl bg-superficie-suave p-1">
        <SelectorPestana
          etiqueta="Pedidos"
          detalle="Del día"
          activa={pestana === 'pedidos'}
          onElegir={() => setPestana('pedidos')}
        />
        <SelectorPestana
          etiqueta="Entregas"
          detalle="Para quién es"
          activa={pestana === 'entregas'}
          onElegir={() => setPestana('entregas')}
        />
        <SelectorPestana
          etiqueta="Cuentas"
          detalle="Quién me debe"
          activa={pestana === 'cuentas'}
          onElegir={() => setPestana('cuentas')}
        />
      </div>

      {pestana === 'cuentas' ? (
        <PanelCuentas cuentas={cuentas} onError={setError} />
      ) : (
        <>
      {/* Un dia a la vez. Las flechas mueven un dia, no una semana. */}
      <div className="flex items-center gap-2">
        <Button
          variante="secundario"
          completo={false}
          aria-label="Día anterior"
          onClick={() => setDiaElegido(sumarDias(dia, -1))}
        >
          ‹
        </Button>
        <div className="flex-1 text-center">
          <p
            className={`text-base font-semibold capitalize ${
              esHoy(dia) ? 'text-marca-fuerte' : 'text-texto'
            }`}
          >
            {nombreDia(dia)} {diaDelMes(dia)}
          </p>
          <p className="text-xs text-texto-tenue">
            {formatearCorto(dia)}
            {(esHoy(dia) || dia === manana) && ` · ${nombreRelativo(dia, referencia)}`}
          </p>
        </div>
        <Button
          variante="secundario"
          completo={false}
          aria-label="Día siguiente"
          onClick={() => setDiaElegido(sumarDias(dia, 1))}
        >
          ›
        </Button>
      </div>

      <TiraDeDias dias={dias} seleccionado={dia} conteo={conteo} onElegir={setDiaElegido} />

      <div className="grid grid-cols-2 gap-2">
        <Button
          variante={esHoy(dia) ? 'primario' : 'secundario'}
          onClick={() => setDiaElegido(referencia)}
        >
          Hoy
        </Button>
        <Button
          variante={dia === manana ? 'primario' : 'secundario'}
          onClick={() => setDiaElegido(manana)}
        >
          Mañana
        </Button>
      </div>

      {pestana === 'pedidos' ? (
        <PanelPedidos
          dia={delDia}
          onEditar={(renglon) => setVista({ modo: 'editar', renglon })}
          onError={setError}
        />
      ) : (
        <PanelEntregas dia={entregas} onError={setError} />
      )}

      <Button tamano="grande" onClick={() => setVista({ modo: 'nuevo', fecha: dia })}>
        + Anotar un pedido para este día
      </Button>

      {/* La semana queda como consulta secundaria, detras de un boton. */}
      <div className="space-y-3 border-t border-borde pt-3">
        <Button
          variante="fantasma"
          aria-expanded={verSemana}
          onClick={() => setVerSemana(!verSemana)}
        >
          {verSemana ? 'Ocultar el resumen de la semana' : 'Resumen de la semana ›'}
        </Button>

        {verSemana && (
          <Card className="bg-superficie-suave">
            <p className="text-xs font-semibold uppercase tracking-wide text-texto-tenue">
              Del {formatearCorto(desde)} al {formatearCorto(hasta)}
            </p>
            {resumenSemana === undefined ? (
              <p className="mt-2 text-sm text-texto-suave">Cargando…</p>
            ) : resumenSemana.cuantos === 0 && resumenSemana.cancelados === 0 ? (
              <p className="mt-2 text-sm text-texto-suave">
                No hay pedidos anotados en esta semana.
              </p>
            ) : (
              <ResumenPedidosCard resumen={resumenSemana} />
            )}
          </Card>
        )}
      </div>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

interface PropsSelector {
  etiqueta: string;
  detalle: string;
  activa: boolean;
  onElegir: () => void;
}

function SelectorPestana({ etiqueta, detalle, activa, onElegir }: PropsSelector) {
  return (
    <button
      type="button"
      aria-pressed={activa}
      onClick={onElegir}
      className={[
        'min-h-11 rounded-lg px-3 py-1.5 transition-colors',
        activa
          ? 'bg-superficie text-texto shadow-sm'
          : 'bg-transparent text-texto-suave active:bg-superficie',
      ].join(' ')}
    >
      <span className="block text-sm font-semibold">{etiqueta}</span>
      <span className="block text-[11px] leading-tight text-texto-tenue">{detalle}</span>
    </button>
  );
}

// ---------------------------------------------------------------------------

interface PropsPanelPedidos {
  dia: DiaDePedidos<RenglonPedido> | undefined;
  onEditar: (renglon: RenglonPedido) => void;
  onError: (mensaje: string | null) => void;
}

/**
 * Vista administrativa: quien me pidio que y quien me debe.
 *
 * Los pendientes de pago van primero porque son la pregunta de esta pantalla.
 * Los pagados NO se esconden: siguen sirviendo para consultar de quien es cada
 * comida.
 */
function PanelPedidos({ dia, onEditar, onError }: PropsPanelPedidos) {
  if (dia === undefined) return <p className="text-sm text-texto-suave">Cargando…</p>;

  if (dia.pedidos.length === 0) {
    return (
      <EstadoVacio
        titulo="Sin pedidos este día"
        descripcion="Anota un pedido y aquí vas a ver quién pidió qué, cuánto debe y si ya se lo llevó."
      />
    );
  }

  const { pendientes, pagados, cancelados } = separarPorCobrar(dia.pedidos);

  const bloques: Array<{ titulo: string | null; pedidos: RenglonPedido[] }> = [
    { titulo: pendientes.length > 0 && pagados.length > 0 ? 'Te deben' : null, pedidos: pendientes },
    { titulo: 'Ya pagaron', pedidos: pagados },
    { titulo: 'Cancelados', pedidos: cancelados },
  ];

  return (
    <div className="space-y-4">
      <Card className="bg-superficie-suave">
        <ResumenPedidosCard resumen={dia.resumen} />
      </Card>

      {bloques
        .filter((bloque) => bloque.pedidos.length > 0)
        .map((bloque) => (
          <div key={bloque.titulo ?? 'pendientes'} className="space-y-2">
            {bloque.titulo !== null && (
              <h2 className="text-xs font-semibold uppercase tracking-wide text-texto-tenue">
                {bloque.titulo}
              </h2>
            )}
            <ul className="space-y-2">
              {bloque.pedidos.map((renglon) => (
                <li key={renglon.id}>
                  <TarjetaPedido
                    renglon={renglon}
                    onEditar={() => onEditar(renglon)}
                    onError={onError}
                  />
                </li>
              ))}
            </ul>
          </div>
        ))}
    </div>
  );
}

// ---------------------------------------------------------------------------

interface PropsPanelEntregas {
  dia: EntregasDeFecha | undefined;
  onError: (mensaje: string | null) => void;
}

/**
 * Vista de servicio: para quien es cada comida y cuales faltan.
 *
 * Sin dinero, a proposito. Aqui se rotulan recipientes, no se cobra. El nombre
 * del cliente es el elemento mas grande de cada renglon porque es el dato que se
 * viene a buscar: cuando cuatro personas pidieron tinga, lo que hace falta saber
 * es de quien es cada porcion.
 */
function PanelEntregas({ dia, onError }: PropsPanelEntregas) {
  if (dia === undefined) return <p className="text-sm text-texto-suave">Cargando…</p>;

  if (dia.platillos.length === 0) {
    return (
      <EstadoVacio
        titulo="Nada que entregar este día"
        descripcion="Cuando anotes pedidos, aquí van a aparecer agrupados por platillo para saber de quién es cada porción."
      />
    );
  }

  const { resumen } = dia;

  return (
    <div className="space-y-5">
      <Card className="bg-superficie-suave">
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 text-sm text-texto-suave">
          <span>
            <strong className="tabular-nums text-texto">{resumen.porciones}</strong>{' '}
            {resumen.porciones === 1 ? 'porción' : 'porciones'}
          </span>
          <span aria-hidden="true">·</span>
          <span>
            <strong className="tabular-nums text-texto">{resumen.platillos}</strong>{' '}
            {resumen.platillos === 1 ? 'platillo' : 'platillos'}
          </span>
        </div>

        <div className="mt-2 flex items-baseline justify-between gap-3">
          <span className="text-sm text-texto-suave">Faltan por entregar</span>
          <span
            className={`text-lg font-bold tabular-nums ${
              resumen.porcionesPorEntregar > 0 ? 'text-aviso' : 'text-exito'
            }`}
          >
            {resumen.porcionesPorEntregar}
          </span>
        </div>
        <div className="mt-0.5 flex items-baseline justify-between gap-3">
          <span className="text-sm text-texto-suave">Ya entregadas</span>
          <span className="font-semibold tabular-nums text-exito">
            {resumen.porcionesEntregadas}
          </span>
        </div>
      </Card>

      {dia.platillos.map((platillo) => (
        <section key={platillo.recipeId} className="space-y-2">
          <div className="flex items-baseline justify-between gap-2">
            <h2
              className={`text-sm font-bold uppercase tracking-wide ${
                platillo.completo ? 'text-texto-tenue' : 'text-texto'
              }`}
            >
              {platillo.nombreReceta}
            </h2>
            <p className="shrink-0 text-xs text-texto-tenue">
              <span className="tabular-nums">{platillo.porciones}</span>{' '}
              {platillo.porciones === 1 ? 'porción' : 'porciones'}
              {platillo.completo && ' · completo'}
            </p>
          </div>

          <ul className="space-y-2">
            {platillo.pedidos.map((renglon) => (
              <li key={renglon.id}>
                <TarjetaEntrega renglon={renglon} onError={onError} />
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------

interface PropsPanelCuentas {
  cuentas: CuentasPorCobrar<RenglonPedido> | undefined;
  onError: (mensaje: string | null) => void;
}

/**
 * Vista de cobranza: quien me debe y cuanto.
 *
 * No depende del dia. La deuda de una persona puede venir de ocho pedidos de
 * ocho dias distintos, y sumarla a mano es justo lo que esta vista evita.
 *
 * El total es CUENTAS POR COBRAR, no ingreso. El ingreso ya se registro al
 * vender; esto es lo que falta que entre a la bolsa.
 */
function PanelCuentas({ cuentas, onError }: PropsPanelCuentas) {
  const [abierto, setAbierto] = useState<string | null>(null);

  if (cuentas === undefined) return <p className="text-sm text-texto-suave">Cargando…</p>;

  if (cuentas.clientes.length === 0) {
    return (
      <EstadoVacio
        titulo="No te deben nada"
        descripcion="Cuando alguien tenga un pedido sin pagar, aquí vas a ver cuánto te debe y de qué días."
      />
    );
  }

  return (
    <div className="space-y-4">
      <Card className="bg-superficie-suave">
        <p className="text-xs font-semibold uppercase tracking-wide text-texto-tenue">Te deben</p>
        <p className="mt-1 text-3xl font-bold tabular-nums text-aviso">
          {formatearCentavos(cuentas.totalCentavos)}
        </p>
        <p className="mt-1 text-sm text-texto-suave">
          {cuentas.clientes.length}{' '}
          {cuentas.clientes.length === 1 ? 'persona' : 'personas'} ·{' '}
          {cuentas.cuantos} {cuentas.cuantos === 1 ? 'pedido' : 'pedidos'} sin pagar
        </p>
      </Card>

      <ul className="space-y-2">
        {cuentas.clientes.map((cuenta) => (
          <li key={cuenta.clienteNombre}>
            <TarjetaCuenta
              cuenta={cuenta}
              abierta={abierto === cuenta.clienteNombre}
              onAlternar={() =>
                setAbierto(abierto === cuenta.clienteNombre ? null : cuenta.clienteNombre)
              }
              onError={onError}
            />
          </li>
        ))}
      </ul>

      <p className="text-xs text-texto-tenue">
        Esto es lo que te falta cobrar, no ingreso. El ingreso ya quedó registrado cuando anotaste
        la venta de cada día.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------

interface PropsTarjetaCuenta {
  cuenta: CuentaDeCliente<RenglonPedido>;
  abierta: boolean;
  onAlternar: () => void;
  onError: (mensaje: string | null) => void;
}

function TarjetaCuenta({ cuenta, abierta, onAlternar, onError }: PropsTarjetaCuenta) {
  const [ocupado, setOcupado] = useState<string | null>(null);

  async function cobrar(id: string) {
    setOcupado(id);
    onError(null);
    try {
      await alternarPago(id);
    } catch (e) {
      onError(mensajeDeError(e));
    } finally {
      setOcupado(null);
    }
  }

  return (
    <Card>
      <button
        type="button"
        aria-expanded={abierta}
        onClick={onAlternar}
        className="-m-1 flex w-full items-start justify-between gap-3 p-1 text-left"
      >
        <div className="min-w-0">
          <p className="break-words text-xl font-bold uppercase leading-tight text-texto">
            {cuenta.clienteNombre}
          </p>
          <p className="mt-0.5 text-xs text-texto-tenue">
            {cuenta.cuantos} {cuenta.cuantos === 1 ? 'pedido' : 'pedidos'} · el más viejo del{' '}
            {formatearCorto(cuenta.desde)}
          </p>
        </div>
        <div className="shrink-0 text-right">
          <p className="text-xl font-bold tabular-nums text-aviso">
            {formatearCentavos(cuenta.totalCentavos)}
          </p>
          <p className="text-xs text-texto-tenue">{abierta ? 'ocultar ▲' : 'ver ▼'}</p>
        </div>
      </button>

      {abierta && (
        <ul className="mt-3 space-y-2 border-t border-borde pt-3">
          {cuenta.pedidos.map((renglon) => (
            <li key={renglon.id} className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm font-medium text-texto">
                  {formatearCorto(renglon.fechaEntrega)} · {renglon.nombreReceta}
                </p>
                <p className="text-xs text-texto-tenue">
                  {renglon.porciones} {renglon.porciones === 1 ? 'porción' : 'porciones'} ·{' '}
                  {formatearCentavos(renglon.totalCentavos)} ·{' '}
                  {renglon.estadoEntrega === 'entregado' ? 'entregado' : 'sin entregar'}
                </p>
              </div>
              <Button
                variante="secundario"
                completo={false}
                disabled={ocupado !== null}
                onClick={() => void cobrar(renglon.id)}
              >
                {ocupado === renglon.id ? '…' : 'Pagó'}
              </Button>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------

interface PropsTarjetaEntrega {
  renglon: RenglonPedido;
  onError: (mensaje: string | null) => void;
}

/**
 * Un cliente y su porcion. El nombre manda.
 *
 * El boton alterna la entrega del MISMO pedido que muestra la vista de Pedidos:
 * es `alternarEntrega`, que solo escribe `estadoEntrega`. No crea registros ni
 * toca ventas, produccion o inventario.
 */
function TarjetaEntrega({ renglon, onError }: PropsTarjetaEntrega) {
  const [ocupado, setOcupado] = useState(false);
  const entregado = renglon.estadoEntrega === 'entregado';

  async function alternar() {
    setOcupado(true);
    onError(null);
    try {
      await alternarEntrega(renglon.id);
    } catch (e) {
      onError(mensajeDeError(e));
    } finally {
      setOcupado(false);
    }
  }

  return (
    <Card className={entregado ? 'bg-superficie-suave' : ''}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p
            className={`break-words text-xl font-bold uppercase leading-tight ${
              entregado ? 'text-texto-tenue' : 'text-texto'
            }`}
          >
            {entregado && <span aria-hidden="true">✓ </span>}
            {renglon.clienteNombre}
          </p>
          {renglon.notas !== null && (
            <p className="mt-1 text-sm italic text-texto-suave">{renglon.notas}</p>
          )}
          {renglon.recetaFaltante && (
            <p className="mt-1 text-xs font-medium text-aviso">
              ⚠ La receta de este pedido ya no existe
            </p>
          )}
        </div>

        <p
          className={`shrink-0 text-right text-sm font-semibold ${
            entregado ? 'text-texto-tenue' : 'text-texto'
          }`}
        >
          <span className="tabular-nums">{renglon.porciones}</span>
          <span className="block text-xs font-normal text-texto-tenue">
            {renglon.porciones === 1 ? 'porción' : 'porciones'}
          </span>
        </p>
      </div>

      <div className="mt-3">
        <Button
          variante={entregado ? 'primario' : 'secundario'}
          disabled={ocupado}
          onClick={() => void alternar()}
        >
          {entregado ? '✓ Entregado' : 'Marcar entregado'}
        </Button>
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------

interface PropsTira {
  dias: readonly FechaLocal[];
  seleccionado: FechaLocal;
  conteo: Map<FechaLocal, number> | undefined;
  onElegir: (fecha: FechaLocal) => void;
}

/**
 * Los siete dias de la semana del dia elegido, con cuantos pedidos tiene cada
 * uno.
 *
 * Es el atajo que evita el scroll: de un vistazo se ve donde hay pedidos y con
 * un toque se cambia de dia.
 */
function TiraDeDias({ dias, seleccionado, conteo, onElegir }: PropsTira) {
  return (
    <ul className="grid grid-cols-7 gap-1">
      {dias.map((fecha) => {
        const elegido = fecha === seleccionado;
        const cuantos = conteo?.get(fecha) ?? 0;

        return (
          <li key={fecha}>
            <button
              type="button"
              aria-pressed={elegido}
              aria-label={
                `${formatearMedio(fecha)}${esHoy(fecha) ? ', hoy' : ''}, ` +
                (cuantos === 1 ? '1 pedido' : `${cuantos} pedidos`)
              }
              onClick={() => onElegir(fecha)}
              className={[
                'flex min-h-11 w-full flex-col items-center justify-center gap-0.5 rounded-xl border py-1.5',
                'transition-colors',
                elegido
                  ? 'border-marca bg-marca text-white'
                  : esHoy(fecha)
                    ? 'border-marca bg-superficie text-marca-fuerte active:bg-marca-suave'
                    : 'border-borde bg-superficie text-texto-suave active:bg-superficie-suave',
              ].join(' ')}
            >
              <span className="text-[10px] uppercase leading-none">
                {nombreDia(fecha).slice(0, 3)}
              </span>
              <span className="text-base font-semibold leading-none tabular-nums">
                {diaDelMes(fecha)}
              </span>
              <span
                aria-hidden="true"
                className={[
                  'flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] font-bold leading-none tabular-nums',
                  cuantos === 0
                    ? 'text-transparent'
                    : elegido
                      ? 'bg-white text-marca-fuerte'
                      : 'bg-marca-suave text-marca-fuerte',
                ].join(' ')}
              >
                {cuantos === 0 ? '·' : cuantos}
              </span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}

// ---------------------------------------------------------------------------

/**
 * Resumen de un dia o de la semana.
 *
 * "Cobrado" y "por cobrar", no "ingreso": esto es lo que los clientes deben, no
 * dinero registrado como venta del negocio.
 */
function ResumenPedidosCard({ resumen }: { resumen: ResumenPedidos }) {
  return (
    <>
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 text-sm text-texto-suave">
        <span>
          <strong className="tabular-nums text-texto">{resumen.cuantos}</strong>{' '}
          {resumen.cuantos === 1 ? 'pedido' : 'pedidos'}
        </span>
        <span aria-hidden="true">·</span>
        <span>
          <strong className="tabular-nums text-texto">{resumen.porciones}</strong>{' '}
          {resumen.porciones === 1 ? 'porción' : 'porciones'}
        </span>
        <span aria-hidden="true">·</span>
        <span>
          <strong className="tabular-nums text-texto">
            {formatearCentavos(resumen.totalCentavos)}
          </strong>{' '}
          en total
        </span>
      </div>

      <div className="mt-2 flex items-baseline justify-between gap-3">
        <span className="text-sm text-texto-suave">Ya te pagaron</span>
        <span className="font-semibold tabular-nums text-exito">
          {formatearCentavos(resumen.cobradoCentavos)}
        </span>
      </div>
      <div className="mt-0.5 flex items-baseline justify-between gap-3">
        <span className="text-sm text-texto-suave">Te deben</span>
        <span
          className={`text-lg font-bold tabular-nums ${
            resumen.porCobrarCentavos > 0 ? 'text-aviso' : 'text-texto'
          }`}
        >
          {formatearCentavos(resumen.porCobrarCentavos)}
        </span>
      </div>

      <p className="mt-2 text-xs text-texto-tenue">
        {resumen.porEntregar} por entregar · {resumen.entregados} ya
        {resumen.entregados === 1 ? ' entregado' : ' entregados'}
        {resumen.cancelados > 0 && ` · ${resumen.cancelados} cancelado${
          resumen.cancelados === 1 ? '' : 's'
        }`}
      </p>
    </>
  );
}

// ---------------------------------------------------------------------------

interface PropsTarjeta {
  renglon: RenglonPedido;
  onEditar: () => void;
  onError: (mensaje: string | null) => void;
}

function TarjetaPedido({ renglon, onEditar, onError }: PropsTarjeta) {
  const [ocupado, setOcupado] = useState(false);
  const cancelado = renglon.estado === 'cancelado';
  const pagado = renglon.estadoPago === 'pagado';
  const entregado = renglon.estadoEntrega === 'entregado';

  async function correr(accion: () => Promise<unknown>) {
    setOcupado(true);
    onError(null);
    try {
      await accion();
    } catch (e) {
      onError(mensajeDeError(e));
    } finally {
      setOcupado(false);
    }
  }

  return (
    <Card className={cancelado ? 'border-dashed bg-superficie-suave' : ''}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p
            className={`font-semibold ${cancelado ? 'text-texto-tenue line-through' : 'text-texto'}`}
          >
            {renglon.clienteNombre}
          </p>
          <p className="text-sm text-texto-suave">
            {renglon.nombreReceta} · {renglon.porciones}{' '}
            {renglon.porciones === 1 ? 'porción' : 'porciones'}
          </p>
          <p className="mt-0.5 text-xs text-texto-tenue">
            {formatearCentavos(renglon.precioPorPorcionCentavos)} cada una
          </p>
          {renglon.notas !== null && (
            <p className="mt-1 text-xs italic text-texto-suave">{renglon.notas}</p>
          )}
          {renglon.recetaFaltante && (
            <p className="mt-1 text-xs font-medium text-aviso">
              ⚠ La receta de este pedido ya no existe
            </p>
          )}
        </div>

        <div className="shrink-0 text-right">
          <p
            className={`font-bold tabular-nums ${
              cancelado ? 'text-texto-tenue line-through' : 'text-texto'
            }`}
          >
            {formatearCentavos(renglon.totalCentavos)}
          </p>
          {cancelado && <p className="text-xs font-medium text-texto-tenue">cancelado</p>}
        </div>
      </div>

      {cancelado ? (
        <div className="mt-3 flex gap-2">
          <Button
            variante="secundario"
            completo={false}
            disabled={ocupado}
            onClick={() => void correr(() => reactivarPedido(renglon.id))}
          >
            Reactivar
          </Button>
          <Button variante="fantasma" completo={false} onClick={onEditar}>
            Ver
          </Button>
        </div>
      ) : (
        <>
          {/* Los dos gestos del dia: cobrar en la puerta y marcar la entrega. */}
          <div className="mt-3 grid grid-cols-2 gap-2">
            <Button
              variante={pagado ? 'primario' : 'secundario'}
              disabled={ocupado}
              onClick={() => void correr(() => alternarPago(renglon.id))}
            >
              {pagado ? '✓ Pagado' : 'Marcar pagado'}
            </Button>
            <Button
              variante={entregado ? 'primario' : 'secundario'}
              disabled={ocupado}
              onClick={() => void correr(() => alternarEntrega(renglon.id))}
            >
              {entregado ? '✓ Entregado' : 'Marcar entregado'}
            </Button>
          </div>

          <div className="mt-2">
            <Button variante="fantasma" completo={false} onClick={onEditar}>
              Editar
            </Button>
          </div>
        </>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------

interface PropsForm {
  renglon?: RenglonPedido;
  fechaInicial: FechaLocal;
  /** Recibe la fecha de entrega con la que quedo el pedido. */
  onListo: (fecha: FechaLocal) => void;
  onCancelar: () => void;
}

const SIN_LIGAR = '';

function PedidoForm({ renglon, fechaInicial, onListo, onCancelar }: PropsForm) {
  const editando = renglon !== undefined;

  const [cliente, setCliente] = useState(renglon?.clienteNombre ?? '');
  const [fecha, setFecha] = useState<FechaLocal>(renglon?.fechaEntrega ?? fechaInicial);
  const [recipeId, setRecipeId] = useState(renglon?.recipeId ?? '');
  /**
   * Arranca en 1, no vacio.
   *
   * Vacio dejaba el boton apagado sin explicar nada: el campo mostraba un "1" de
   * marca de agua que parecia un valor ya escrito, asi que no habia forma de
   * adivinar que faltaba llenarlo. Y casi todos los pedidos son de una porcion.
   */
  const [porciones, setPorciones] = useState(
    renglon === undefined ? '1' : String(renglon.porciones),
  );
  const [precio, setPrecio] = useState(
    renglon === undefined ? '' : String(centavosAPesos(renglon.precioPorPorcionCentavos)),
  );
  const [plannedItemId, setPlannedItemId] = useState(renglon?.plannedItemId ?? SIN_LIGAR);
  const [estadoPago, setEstadoPago] = useState(renglon?.estadoPago ?? 'pendiente');
  const [estadoEntrega, setEstadoEntrega] = useState(renglon?.estadoEntrega ?? 'pendiente');
  const [notas, setNotas] = useState(renglon?.notas ?? '');

  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmandoBorrado, setConfirmandoBorrado] = useState(false);

  const recetas = useLiveQuery(() => recetasParaPedido(), []);
  const clientes = useLiveQuery(() => clientesUsados(), []);
  const platillos = useLiveQuery(() => platillosParaLigar(fecha), [fecha]);

  /**
   * El precio que ya se le ha cobrado a este cliente por este platillo.
   *
   * Para los precios de cliente frecuente: Juan lleva años comprando pollo a la
   * plancha a $100 y no a los $120 de la lista. Se ofrece, no se impone: el
   * campo se puede sobrescribir.
   */
  const precioDelCliente = useLiveQuery(
    () => (recipeId === '' ? Promise.resolve(null) : precioUsadoCon(cliente, recipeId)),
    [cliente, recipeId],
  );

  const porcionesNumero = Number(porciones);
  const precioNumero = precio === '' ? Number.NaN : Number(precio);
  const elegida = recetas?.find((r) => r.id === recipeId);

  /** true si a este cliente se le cobra distinto del precio de lista. */
  const esPrecioEspecial =
    precioDelCliente !== null &&
    precioDelCliente !== undefined &&
    elegida !== undefined &&
    precioDelCliente !== elegida.precioVentaCentavos;

  /**
   * Que falta para poder guardar, en palabras.
   *
   * Se calcula la lista y no solo un booleano para poder DECIRLO debajo del
   * boton. Un boton apagado sin motivo deja a la usuaria sin salida: no hay
   * mensaje de error que leer porque nunca se intento guardar.
   */
  const faltan: string[] = [];
  if (cliente.trim() === '') faltan.push('el nombre del cliente');
  if (recipeId === '') faltan.push('el platillo');
  if (!Number.isFinite(porcionesNumero) || porcionesNumero <= 0) faltan.push('las porciones');

  const valida = faltan.length === 0;

  /**
   * El precio en blanco significa "el que le corresponde a este cliente", no
   * cero: primero el que ya se le cobro antes, y si nunca, el de la receta.
   */
  const precioPorOmision =
    precioDelCliente !== null && precioDelCliente !== undefined
      ? precioDelCliente
      : (elegida?.precioVentaCentavos ?? 0);

  const precioCentavos =
    precio === ''
      ? precioPorOmision
      : Number.isFinite(precioNumero)
        ? pesosACentavos(precioNumero)
        : 0;
  const totalPrevisto = Number.isFinite(porcionesNumero)
    ? Math.round(porcionesNumero * precioCentavos)
    : 0;

  async function guardar() {
    setGuardando(true);
    setError(null);
    try {
      const comunes = {
        clienteNombre: cliente,
        fechaEntrega: fecha,
        recipeId,
        porciones: porcionesNumero,
        plannedItemId: plannedItemId === SIN_LIGAR ? null : plannedItemId,
        estadoPago,
        estadoEntrega,
        notas: notas.trim() === '' ? null : notas,
      };

      if (editando) {
        await editarPedido(renglon.id, {
          ...comunes,
          // Al editar se manda el precio siempre, para poder corregirlo.
          precioPorPorcionCentavos: precioCentavos,
        });
      } else {
        await crearPedido({
          ...comunes,
          // Sin capturar, el repositorio toma el precio de la receta.
          ...(precio === '' ? {} : { precioPorPorcionCentavos: precioCentavos }),
        });
      }
      onListo(fecha);
    } catch (e) {
      setError(mensajeDeError(e));
    } finally {
      setGuardando(false);
    }
  }

  async function correr(accion: () => Promise<unknown>) {
    setGuardando(true);
    setError(null);
    try {
      await accion();
      onListo(fecha);
    } catch (e) {
      setError(mensajeDeError(e));
      setGuardando(false);
    }
  }

  if (recetas !== undefined && recetas.length === 0) {
    return (
      <div className="space-y-4">
        <PageHeader titulo="Anotar un pedido" />
        <EstadoVacio
          titulo="Todavía no hay recetas"
          descripcion="Los pedidos se anotan sobre tus platillos. Crea una receta primero y aquí va a aparecer en la lista."
        >
          <Button variante="secundario" onClick={onCancelar}>
            Volver
          </Button>
        </EstadoVacio>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <PageHeader
        titulo={editando ? 'Editar pedido' : 'Anotar un pedido'}
        descripcion={editando ? renglon.clienteNombre : undefined}
      />

      {error !== null && (
        <Banner tono="alerta" titulo="No se pudo guardar">
          {error}
        </Banner>
      )}

      {/*
        Los clientes ya vienen sin repetidos: "Juan" y "juan" llegan como uno.
        El campo avisa si el nombre escrito es alguien que ya existe con otra
        escritura, para no seguir creando variantes.
      */}
      <CampoCliente
        valor={cliente}
        onCambio={setCliente}
        clientes={clientes ?? []}
        autoFocus={!editando}
      />

      <Buscador
        etiqueta="Platillo"
        valor={recipeId === '' ? null : recipeId}
        opciones={(recetas ?? []).map((r) => ({
          valor: r.id,
          etiqueta: r.nombre,
          detalle: formatearCentavos(r.precioVentaCentavos),
        }))}
        onCambio={(valor) => {
          setRecipeId(valor ?? '');
          // Al cambiar de platillo se suelta el enlace con el menu, que era del
          // platillo anterior.
          setPlannedItemId(SIN_LIGAR);
        }}
        ayuda="Escribe parte del nombre para buscar. Por ejemplo “po” encuentra pollo y pozole."
      />

      <DateField etiqueta="Fecha de entrega" valor={fecha} onCambio={(v) => setFecha(v)} />

      <NumberField
        etiqueta="Porciones"
        valor={porciones}
        onCambio={setPorciones}
        placeholder="1"
        decimal={false}
      />

      <MoneyField
        etiqueta="Precio por porción"
        valor={precio}
        onCambio={setPrecio}
        ayuda={
          esPrecioEspecial
            ? `A ${cliente.trim()} le cobras ${formatearCentavos(precioDelCliente)} este platillo. ` +
              `Vacío usa ese precio; el de lista es ${formatearCentavos(elegida.precioVentaCentavos)}.`
            : elegida === undefined
              ? 'Si lo dejas vacío se usa el precio de la receta'
              : `Vacío usa el de la receta: ${formatearCentavos(elegida.precioVentaCentavos)}`
        }
      />

      {valida && (
        <Card className="bg-superficie-suave">
          <div className="flex items-baseline justify-between gap-3">
            <span className="text-sm text-texto-suave">Total del pedido</span>
            <span className="text-2xl font-bold tabular-nums text-marca-fuerte">
              {formatearCentavos(totalPrevisto)}
            </span>
          </div>
          <p className="mt-1 text-xs text-texto-tenue">
            Es lo que el cliente debe. No cuenta como ingreso hasta que registres la venta.
          </p>
        </Card>
      )}

      <SelectField
        etiqueta="Pago"
        valor={estadoPago}
        onCambio={(v) => setEstadoPago(v as typeof estadoPago)}
        opciones={Object.entries(ESTADOS_PAGO).map(([valor, etiqueta]) => ({ valor, etiqueta }))}
      />

      <SelectField
        etiqueta="Entrega"
        valor={estadoEntrega}
        onCambio={(v) => setEstadoEntrega(v as typeof estadoEntrega)}
        opciones={Object.entries(ESTADOS_ENTREGA).map(([valor, etiqueta]) => ({ valor, etiqueta }))}
      />

      {platillos !== undefined && platillos.length > 0 && (
        <SelectField
          etiqueta="Ligar al menú de ese día"
          valor={plannedItemId}
          onCambio={setPlannedItemId}
          opciones={[
            { valor: SIN_LIGAR, etiqueta: 'Sin ligar' },
            ...platillos.map((p) => ({
              valor: p.id,
              etiqueta: `${p.nombre} (${p.porcionesPlaneadas} porciones planeadas)`,
            })),
          ]}
          ayuda="Opcional. Ligarlo no cambia las porciones del menú."
        />
      )}

      <TextField
        etiqueta="Nota"
        valor={notas}
        onCambio={setNotas}
        placeholder="Opcional: sin cebolla, pasa a las 2…"
        maxLength={200}
      />

      <div className="space-y-2">
        <Button
          tamano="grande"
          disabled={!valida || guardando}
          onClick={() => void guardar()}
        >
          {guardando ? 'Guardando…' : editando ? 'Guardar cambios' : 'Anotar el pedido'}
        </Button>

        <Falta faltan={faltan} />

        <Button variante="secundario" disabled={guardando} onClick={onCancelar}>
          Cancelar
        </Button>
      </div>

      {editando && (
        <Seccion
          titulo="Quitar este pedido"
          descripcion="Cancelar conserva el registro. Eliminar lo borra."
        >
          {renglon.estado === 'activo' ? (
            <Button
              variante="secundario"
              disabled={guardando}
              onClick={() => void correr(() => cancelarPedido(renglon.id))}
            >
              El cliente canceló
            </Button>
          ) : (
            <Button
              variante="secundario"
              disabled={guardando}
              onClick={() => void correr(() => reactivarPedido(renglon.id))}
            >
              Reactivar el pedido
            </Button>
          )}

          {confirmandoBorrado ? (
            <Banner tono="alerta" titulo="¿Eliminar el pedido?">
              Se borra por completo y no se puede recuperar. Si el cliente canceló, mejor usa
              “El cliente canceló” para conservar el registro.
              <div className="mt-3 space-y-2">
                <Button
                  variante="peligro"
                  disabled={guardando}
                  onClick={() => void correr(() => eliminarPedido(renglon.id))}
                >
                  Sí, eliminar
                </Button>
                <Button
                  variante="secundario"
                  disabled={guardando}
                  onClick={() => setConfirmandoBorrado(false)}
                >
                  No, dejarlo
                </Button>
              </div>
            </Banner>
          ) : (
            <Button
              variante="fantasma"
              disabled={guardando}
              onClick={() => setConfirmandoBorrado(true)}
            >
              Lo capturé por error, eliminar
            </Button>
          )}
        </Seccion>
      )}
    </div>
  );
}
